import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  IconButton,
  Typography,
  Divider,
  MenuItem,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment';
import constants from '../utilities/constants';
import firebase from '../utilities/firebase';
import { StateManager } from '../utilities/stateManager';
import Store from '../utilities/store';
import { useStackZIndex, useSelectMenuProps, ModalStackProvider } from '../utilities/ModalStackContext';

// Parts whose total (cost × quantity) is at or below this are approved on
// creation when they are linked to an open service line and the service order
// is past estimate. Keep in sync with skyway-functions parts/autoApprove.js.
const AUTO_APPROVE_LIMIT = 199;

// Picker value for a part that is not for any service line.
const NO_LINE = 'none';

const EMPTY_ROW = { name: '', vendor: '', partNumber: '', link: '', quantity: 1, cost: '', notes: '', serviceId: '' };

// A row counts as empty (and is silently skipped on submit) when the user
// hasn't entered anything meaningful — quantity defaulting to 1 doesn't count.
const isRowEmpty = (row) =>
  !row.name.trim() &&
  !row.vendor.trim() &&
  !String(row.partNumber).trim() &&
  !row.link.trim() &&
  !String(row.cost).trim() &&
  !row.notes.trim();

const OrderPartDialog = ({ open, onClose, order, onSuccess }) => {
  const myZ = useStackZIndex();
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [serviceLines, setServiceLines] = useState([]);
  const selectMenuProps = useSelectMenuProps(myZ);

  // The picker lists only open lines, so any line chosen here is open.
  useEffect(() => {
    if (!open || !order?.id) return undefined;
    let active = true;
    Store.getOpenServiceLines(order.id)
      .then((lines) => { if (active) setServiceLines(lines); })
      .catch((err) => {
        console.error('Load service lines error:', err);
        StateManager.setAlertAndOpen('Could not load service lines', 'error');
      });
    return () => { active = false; };
  }, [open, order?.id]);

  const isBackoffice = StateManager.isBackoffice();

  const handleRowChange = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_ROW }]);

  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

  const resetAndClose = () => {
    setRows([{ ...EMPTY_ROW }]);
    setError('');
    setSubmitting(false);
    if (onClose) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const filledRows = rows.filter((row) => !isRowEmpty(row));
    if (filledRows.length === 0) {
      setError('Add at least one part.');
      return;
    }

    // Per-row validation mirrors the single-part rules.
    for (let i = 0; i < filledRows.length; i++) {
      const row = filledRows[i];
      const label = filledRows.length > 1 ? `Part ${i + 1}: ` : '';
      if (!row.name.trim() && !isBackoffice) {
        setError(`${label}Part name is required.`);
        return;
      }
      if (!row.serviceId) {
        setError(`${label}Pick the service line this part is for, or "Not for a service line".`);
        return;
      }
      if (!isBackoffice) {
        const hasLink = row.link && row.link.trim() !== '';
        const hasVendorAndPartNumber =
          row.vendor && row.vendor.trim() !== '' && row.partNumber && String(row.partNumber).trim() !== '';
        if (!hasLink && !hasVendorAndPartNumber) {
          setError(`${label}Provide either a part link OR both vendor and part number.`);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      // Create each part in one batch. Their linked purchases (purchases/PART-{id})
      // are created automatically by the syncPartPurchase Cloud Function trigger.
      const db = firebase.firestore();
      const batch = db.batch();
      const now = moment().format('YYYY/MM/DD');
      // Work on an order still at estimate (or earlier) isn't sold yet, so its
      // parts always wait for manual approval. Unknown statuses count as unsold.
      const orderIsSold =
        constants.order_statuses.indexOf(order?.status) >
        constants.order_statuses.indexOf('estimate');
      // Managers can switch auto-approve off on the Parts Pipeline page.
      // A failed read leaves it on, as before the switch existed.
      const settings = await Store.getPartsSettings().catch(() => ({}));
      const autoApproveOn = settings.autoApprove !== false;
      filledRows.forEach((row) => {
        const ref = db.collection('parts').doc();
        const quantity = parseInt(row.quantity, 10) || 1;
        const cost = parseFloat(row.cost) || 0;
        // Auto-approve parts with a known total within the limit. A cost of 0
        // means no price was entered, so those parts stay pending for review.
        const total = cost * quantity;
        const serviceId = row.serviceId === NO_LINE ? null : row.serviceId;
        const autoApprove = autoApproveOn && !!serviceId && orderIsSold && cost > 0 && total <= AUTO_APPROVE_LIMIT;
        batch.set(ref, {
          name: row.name,
          vendor: row.vendor,
          partNumber: row.partNumber,
          link: row.link,
          notes: row.notes,
          quantity,
          cost,
          date: now,
          status: autoApprove ? 'approved' : 'pending',
          ...(autoApprove ? { status_time: now } : {}),
          serviceId,
          order: order?.id || '',
        });
      });
      await batch.commit();

      StateManager.setAlertAndOpen(
        `Added ${filledRows.length} part${filledRows.length > 1 ? 's' : ''}`,
        'success'
      );
      resetAndClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      setError('Failed to order parts. Please try again.');
      console.error('Order part error:', err);
      setSubmitting(false);
    }
  };

  return (
    <ModalStackProvider value={myZ}>
      <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth sx={{ zIndex: myZ }}>
        <DialogTitle>Part request for {order?.car_title || ''}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {rows.map((row, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                {index > 0 && <Divider sx={{ mb: 2 }} />}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Part {index + 1}
                  </Typography>
                  {rows.length > 1 && (
                    <IconButton size="small" onClick={() => removeRow(index)} disabled={submitting} aria-label="Remove part">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Service line"
                      value={row.serviceId}
                      onChange={(e) => handleRowChange(index, 'serviceId', e.target.value)}
                      fullWidth
                      required
                      SelectProps={{ MenuProps: selectMenuProps }}
                      helperText={serviceLines.length ? 'The open line this part is for' : 'This SO has no open service lines'}
                    >
                      {serviceLines.map((line) => (
                        <MenuItem key={line.id} value={line.id}>{line.name}</MenuItem>
                      ))}
                      <MenuItem value={NO_LINE}><em>Not for a service line</em></MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Name"
                      value={row.name}
                      onChange={(e) => handleRowChange(index, 'name', e.target.value)}
                      fullWidth
                      required={!isBackoffice}
                      helperText={isBackoffice ? 'Optional' : 'Required'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Vendor"
                      value={row.vendor}
                      onChange={(e) => handleRowChange(index, 'vendor', e.target.value)}
                      fullWidth
                      helperText={isBackoffice ? 'Optional' : 'Required if no link provided'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Part Number"
                      value={row.partNumber}
                      onChange={(e) => handleRowChange(index, 'partNumber', e.target.value)}
                      fullWidth
                      helperText={isBackoffice ? 'Optional' : 'Required if no link provided'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Link"
                      value={row.link}
                      onChange={(e) => handleRowChange(index, 'link', e.target.value)}
                      fullWidth
                      helperText="Alternative to vendor/part number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Quantity"
                      type="number"
                      value={row.quantity}
                      onChange={(e) => handleRowChange(index, 'quantity', e.target.value)}
                      fullWidth
                      inputProps={{ min: 1 }}
                      required={!isBackoffice}
                      helperText={isBackoffice ? 'Optional' : 'Required'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Cost"
                      type="number"
                      value={row.cost}
                      onChange={(e) => handleRowChange(index, 'cost', e.target.value)}
                      fullWidth
                      inputProps={{ min: 0, step: 0.01 }}
                      required={!isBackoffice}
                      helperText={isBackoffice ? 'Optional' : 'Required'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Notes"
                      value={row.notes}
                      onChange={(e) => handleRowChange(index, 'notes', e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Button startIcon={<AddIcon />} onClick={addRow} disabled={submitting} sx={{ mt: 1 }}>
              Add another part
            </Button>

            {error && (
              <Box mt={2}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={resetAndClose} color="inherit" disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={submitting}>
              {submitting ? 'Submitting...' : `Submit${rows.filter((r) => !isRowEmpty(r)).length > 1 ? ` (${rows.filter((r) => !isRowEmpty(r)).length})` : ''}`}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </ModalStackProvider>
  );
};

export default OrderPartDialog;
